import { httpsCallable } from 'firebase/functions';
import { useEffect, useRef, useState } from 'react';

import { fns } from '@/integrations/firebase/functions';
import type { LibraryStatus } from '@/lib/library-status';

// "unknown" is reserved by the server; the UI renders no status text for it.
export type SuggestionStatus = LibraryStatus | 'unknown';

// One active substance of a combination, with the mono ATC a search would run
// for (null when RPL has no standalone product — the UI then submits the
// substance name and lets RxNorm map it).
export type DrugSuggestionComponent = {
  substance: string;
  atc: string | null;
  status: SuggestionStatus;
  drug_id: string | null;
};

// One report the user can open: a substance across every ATC it appears under,
// or one fixed combination. The server owns grouping, ranking and the stable
// `key`; the client renders rows 1:1.
export type DrugSuggestionGroup = {
  key: string;
  // Primary level-5 ATC (the one with most products); null for an alias-only
  // group without a registry product.
  atc: string | null;
  atc_codes: string[];
  substance: string;
  substances: string[];
  is_combination: boolean;
  status: SuggestionStatus;
  // Always set when `status` is "ready".
  drug_id: string | null;
  // The brand that matched when the visible label did not, shown inline.
  matched_brand: string | null;
  // Set only when the typed text matched an alias key.
  matched_alias: string | null;
  // Every human product in the group, not only the matched ones.
  total_products: number;
  // True when the response came from the similarity fallback (nothing matched
  // the typed text). All-or-nothing per response.
  fuzzy: boolean;
  // Combinations only: false when the mixture has more active substances than
  // the resolver's fan-out cap, so a joint report would never generate. The UI
  // then offers only per-ingredient picks. Absent on mono groups.
  reportable_together?: boolean;
  components?: DrugSuggestionComponent[];
};

type SuggestResponse = {
  groups: DrugSuggestionGroup[];
};

// The groups on screen plus the trimmed query that produced them, kept
// together so `stale` never compares rows from one lookup with the query of
// another.
type SuggestionResult = {
  query: string;
  groups: DrugSuggestionGroup[];
};

const NO_RESULT: SuggestionResult = { query: '', groups: [] };

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

export function useDrugSuggestions(query: string, enabled = true) {
  const trimmed = query.trim();
  const active = enabled && trimmed.length >= MIN_QUERY_LENGTH;
  const [result, setResult] = useState<SuggestionResult>(NO_RESULT);
  const [loading, setLoading] = useState(active);
  // Distinct from "empty results": the callable threw (offline / backend error),
  // so the dropdown can show a retryable error state instead of silent guidance.
  const [error, setError] = useState(false);
  // Bumped by retry() to re-run the effect for the same query after a failure.
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);
  const callableRef = useRef(
    httpsCallable<{ q: string; limit?: number }, SuggestResponse>(
      fns,
      'drug_suggest',
    ),
  );

  // Synchronous state resets happen during render (React's derived-state
  // pattern), not in the effect: a new active query marks loading and clears
  // the error while the previous groups stay on screen (stale-while-revalidate);
  // dropping below the minimum / disabling clears everything.
  const resetKey = `${active ? '1' : '0'}${trimmed}${reloadKey}`;
  const [trackedKey, setTrackedKey] = useState(resetKey);
  if (resetKey !== trackedKey) {
    setTrackedKey(resetKey);
    setError(false);
    if (active) {
      setLoading(true);
    } else {
      setResult(NO_RESULT);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!active) return;

    const requestId = ++requestIdRef.current;

    const timer = window.setTimeout(async () => {
      try {
        const response = await callableRef.current({ q: trimmed, limit: 8 });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResult({ query: trimmed, groups: response.data.groups ?? [] });
        setError(false);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        console.error('Drug suggestion fetch failed:', err);
        setResult(NO_RESULT);
        setError(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [active, trimmed, reloadKey]);

  const retry = () => setReloadKey((k) => k + 1);

  // Rows kept on screen from an earlier query while the current one loads.
  const stale =
    active && result.groups.length > 0 && result.query !== trimmed;

  return { groups: result.groups, loading, error, retry, stale };
}
